import { isQrveyTestEnvironment } from '../config.js'
import { useState } from 'react'
import { Modal, PlusIcon } from './modal.jsx'
import { isRole, roleHasCapability, tenantIdProblem } from '@qim/shared'
import { ApiError, createTenant } from '../api.js'
import type { FormEvent, JSX } from 'react'
import type { TenantKind } from '@qim/shared'

/**
 * Creating a customer.
 *
 * Until IM-63 this was a `curl`, which meant every engagement began outside the
 * console. Nothing about it is difficult; it was simply never built.
 *
 * **The id is validated by the rule the API uses**, not by one written here.
 * `tenantIdProblem` sits in `shared` beside `KEY_SAFE_PATTERN`, which
 * `assertKeySafe` also imports — so the form and the storage layer cannot
 * disagree about what is acceptable, and a refusal arrives before the request
 * rather than as a 400 someone has to interpret.
 *
 * **The id cannot be changed afterwards.** It is the partition key for
 * everything the customer owns, so the field says so rather than letting someone
 * discover it later.
 *
 * **Nor can the kind** (IM-149). Whether a customer is a real customer or
 * Qrvey's own decides their grant default and whether the daily digest reports
 * on them, so in production it has no default: it is asked every time rather
 * than inherited by whoever did not notice the choice.
 *
 * In dev and staging it starts on Qrvey's own (IM-180, Amit, 2026-09-30).
 * Those consoles deploy only Qrvey's test instances, so a blank choice there
 * was a click every time for the answer that is nearly always right. It is
 * still shown and still one click to change, and production is untouched.
 */
export const NewCustomer = ({
  baseUrl,
  role,
  environment,
  onCreated,
}: {
  readonly baseUrl: string
  readonly role: string
  /** `AppConfig.environment`; decides whether the kind starts chosen. */
  readonly environment: string
  /** Refreshes the list, so the new customer appears without a reload. */
  readonly onCreated: () => void
}): JSX.Element | null => {
  const [tenantId, setTenantId] = useState('')
  const [name, setName] = useState('')
  const startingKind: TenantKind | undefined = isQrveyTestEnvironment(environment)
    ? 'qrvey'
    : undefined
  const [kind, setKind] = useState<TenantKind | undefined>(startingKind)
  const [busy, setBusy] = useState(false)
  const [open, setOpen] = useState(false)
  const [failure, setFailure] = useState<string | undefined>(undefined)

  // Not a control — the API checks the capability and would refuse anyway. But
  // offering someone a form that always fails is a worse way to say no than not
  // offering it.
  if (!isRole(role) || !roleHasCapability(role, 'tenant:manage')) return null

  const problem = tenantId === '' ? undefined : tenantIdProblem(tenantId)
  const ready = tenantId !== '' && name.trim() !== '' && problem === undefined && kind !== undefined

  const submit = async (event: FormEvent): Promise<void> => {
    event.preventDefault()
    if (!ready || kind === undefined) return
    setBusy(true)
    setFailure(undefined)
    try {
      await createTenant(baseUrl, tenantId, name, kind)
      setTenantId('')
      setName('')
      setKind(startingKind)
      // Closed on success only: a refusal keeps the dialog and what was typed,
      // so the fix is an edit rather than filling the form in again.
      setOpen(false)
      onCreated()
    } catch (error: unknown) {
      setFailure(
        error instanceof ApiError && error.status === 409
          ? `A customer with the id ${tenantId} already exists.`
          : error instanceof ApiError && error.status === 400
            ? 'The control plane refused those details.'
            : 'Could not create the customer just now.',
      )
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <div className="panel-action">
        <button type="button" className="primary" onClick={() => setOpen(true)}>
          <PlusIcon />
          New customer
        </button>
      </div>
    )
  }

  return (
    /*
     * A modal rather than a `<details>` on the page (IM-146).
     *
     * Creating a customer is a deliberate, self-contained act with nothing on
     * the list behind it to refer to, and the disclosure version put a form in
     * the middle of a table people were reading. The modal also gives the first
     * field focus, which the disclosure could not without stealing it on every
     * render.
     */
    <Modal title="New customer" onCancel={() => setOpen(false)}>
      <form onSubmit={(event) => void submit(event)}>
        <div className="field">
          <label htmlFor="tenantId">Customer id</label>
          <input
            id="tenantId"
            value={tenantId}
            autoFocus
            onChange={(event) => setTenantId(event.target.value)}
            aria-describedby="tenantId-hint"
          />
          <span id="tenantId-hint" className="hint">
            Letters, digits, dots, hyphens and underscores. This is permanent &mdash; it keys
            everything the customer owns.
          </span>
          {problem === undefined ? null : <span className="failure">{problem}</span>}
        </div>
        <div className="field">
          <label htmlFor="tenantName">Name</label>
          <input id="tenantName" value={name} onChange={(event) => setName(event.target.value)} />
        </div>
        <fieldset className="field kind-choice" aria-describedby="tenantKindHint">
          <legend>Whose is it?</legend>
          <label htmlFor="tenantKindCustomer">
            <input
              id="tenantKindCustomer"
              type="radio"
              name="tenantKind"
              checked={kind === 'customer'}
              onChange={() => setKind('customer')}
            />{' '}
            A customer
          </label>
          <label htmlFor="tenantKindQrvey">
            <input
              id="tenantKindQrvey"
              type="radio"
              name="tenantKind"
              checked={kind === 'qrvey'}
              onChange={() => setKind('qrvey')}
            />{' '}
            Qrvey&rsquo;s own
          </label>
          <span id="tenantKindHint" className="hint">
            Qrvey&rsquo;s own customers get a year-long grant template and appear in the daily
            digest of instances past their lifetime. This is permanent &mdash; it cannot be changed
            after the customer is created.
          </span>
        </fieldset>
        {failure === undefined ? null : <p className="failure">{failure}</p>}
        <div className="actions">
          <button type="button" className="ghost" onClick={() => setOpen(false)}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={busy || !ready}>
            {busy ? 'Creating…' : 'Create customer'}
          </button>
        </div>
      </form>
    </Modal>
  )
}
